import { useMutation } from '@apollo/client/react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@optools/components/ui/sheet';
import { CREATE_AUDIENCE } from '@optools/services/operator-gateway/esni/mutations';
import { GET_AUDIENCES } from '@optools/services/operator-gateway/esni/queries';
import { AudiencesQuery } from '@optools/services/operator-gateway/types/generated/graphql';
import { toast } from 'sonner';

import { AudienceForm, AudienceFormValues } from './audience-form';

export interface CreateAudienceSheetProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export function CreateAudienceSheet({ onOpenChange, open }: CreateAudienceSheetProps) {
  const [createAudience, { loading }] = useMutation(CREATE_AUDIENCE, {
    onCompleted: ({ createAudience }) => {
      if (!createAudience) {
        toast.error('Failed to create audience', {
          description: `Audience could not be created`,
          duration: Infinity,
        });
        return;
      }

      toast.success('Created audience successfully', {
        description: `Created audience: ${createAudience.href}`,
        duration: Infinity,
      });
      onOpenChange(false);
    },
    update: (cache, { data }) => {
      if (!data?.createAudience) return;

      const audiencesQuery = cache.readQuery<AudiencesQuery>({
        query: GET_AUDIENCES,
      });

      if (!audiencesQuery) return;

      const updatedAudiences = [data.createAudience, ...audiencesQuery.audiences];
      cache.writeQuery<AudiencesQuery>({
        data: { audiences: updatedAudiences },
        query: GET_AUDIENCES,
      });
    },
  });

  const handleSubmit = (values: AudienceFormValues) => {
    createAudience({ variables: { audience: values } });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-(--breakpoint-sm)">
        <SheetTitle className="mb-6">Create Audience</SheetTitle>
        <SheetDescription className="sr-only">Create a new audience</SheetDescription>
        <AudienceForm
          loading={loading}
          onCancel={() => onOpenChange(false)}
          onSubmit={handleSubmit}
        />
      </SheetContent>
    </Sheet>
  );
}
